/**
 * The access-control vocabulary (ADR-034), read by the backend, the CLI, the admin UI and the web app.
 *
 * A permission is `noun.verb`. Flat — no wildcards, no permission implying another. A permission that
 * silently confers others cannot be read off a role listing, and making a role's grants legible at a
 * glance is the entire job of the admin screen this vocabulary feeds.
 *
 * The vocabulary lives here rather than in a database table on purpose. A table would have to be
 * seeded in lockstep with the code that names these strings, and the two would drift the first time a
 * migration was written but not run. Roles and bindings ARE rows, because they are configuration; the
 * vocabulary is not configuration.
 */
import { ACCESS_APPROVER_ROLE_KEY } from './access-request-approvers.js';
import { permissions as permissionsMessages } from './i18n/messages/en/permissions.js';

export const SUBJECT_TYPES = ['user', 'agent'] as const;
export type SubjectTypeType = (typeof SUBJECT_TYPES)[number];

/**
 * Where a binding applies. Three levels, widest first.
 *
 * ⭐ **This used to say two**, with the reason that there was nothing between an installation and a
 * work area, so a middle tier would be one nobody could name. That reasoning was sound for the
 * product it described, and it is kept in `concept-access-control.md` §2 because it is the sentence
 * the reversal had to answer. **A tier has since been named**: a namespace — a person or an
 * organisation, one pool of paths — owns every work area (ADR-089), and ADR-092 §3 is where this
 * value was decided.
 *
 * Order is widest to narrowest, and it is the order the concept uses. Nothing reads it as a rank:
 * coverage is decided by `bindingsGrant`, never by an index into this array, because a ladder that
 * means something only when you know its order is a ladder that inverts the first time somebody
 * sorts it.
 */
export const PERMISSION_SCOPES = ['system', 'namespace', 'work_area'] as const;
export type PermissionScopeType = (typeof PERMISSION_SCOPES)[number];

export const PERMISSIONS = [
  // A verified person's single-artifact grant, intersected with credential scope; not membership.
  'artifact_share.read',
  'artifact_share.contribute',
  'file_review.read',
  'file_review.contribute',
  'file_review.manage',
  'work_item.read',
  'work_item.read_shared',
  'work_item.create',
  'work_item.update',
  'work_item.delete',
  'work_item.comment',
  'work_item.attach',
  // F-0542 (`concept-access-control.md` §23): what a SUBAGENT of the subject may write. Asked only of
  // a write labelled with a subagent, on top of the act's own permission.
  'subagent.comment',
  'subagent.update_held',
  'subagent.close_as_verifier',
  'release.read',
  'release.manage',
  // F-0422 (`concept-scheduled-work.md` SW-57): defining, changing and retiring a schedule.
  'schedule.manage',
  // §8.5 (D-h): asking a machine for one run of a schedule's entry now, beside its cadence.
  'schedule.run',
  // F-0694 (SCH-3, §10): a machine's bridge reporting the schedules it holds and the runs its engine
  // recorded. Report only; it defines nothing.
  'schedule.report',
  // F-0799 (§13a.7, D-0494 a): making or changing a schedule FEATit triggers that instructs agents.
  // A person's act only: no agent ceiling or agent role carries it.
  'schedule.instruct',
  'agent.read',
  'agent.create',
  'agent.approve',
  // ⛔ No `agent.merge` (I-0136 consequence 2, revised 2026-10-03). It briefly named merging one's
  // own agents, which `AgentsService.mergeForUser` already gates on ownership alone — nothing asks
  // a permission for it, so declaring one here would be exactly the hazard §6.1/§12.4 describe: a
  // System Admin toggling it on a role and seeing nothing change. There is no cross-owner merge
  // capability to gate instead; see the migration that deletes its grants
  // (`1791320000000-AgentMergeLeavesTheVocabulary`) and `concept-access-control.md` §11.4 AC-G's
  // second revision. If a cross-owner override is ever built, the permission returns together with
  // its check, never one without the other.
  'agent.input',
  // F-0654 (`concept-fleet-pause-and-budgets.md` §5): pausing and resuming other people's agents, or
  // the whole work area. One's own agents never need it.
  'agent.pause',
  'work_area.read',
  'work_area.manage',
  // F-0568 (ADR-168 §8, `concept-file-grants.md` §11.5): adding a folder on one's OWN device to a
  // work area. Personal: it counts for the person's own sessions. Contributor and up, not the viewer.
  'work_area.folder_declare',
  'work_area.export',
  'work_area.publish',
  // T-0604 (D-0125, `concept-delegated-operation.md` §12): switching on a work area's setting that
  // lets its agents pause and resume trust sources. The Owner's alone, as `work_area.publish` is.
  'trust_source.allow_agents',
  // F-0709 (`concept-start-a-work-area-as-if.md` §0 D2): two permissions of their own. Turning a work
  // area's as-if setting on or off is the Owner's; starting a work area as another role is the
  // admin's and the Owner's, where the setting is on. Neither raises anyone above their own grade:
  // an as-if credential holds `holder ∩ target ∩ ceiling` (`asIfEffectivePermissions`).
  'as_if.allow',
  'as_if.start',
  // F-0713 (ADR-187, `concept-work-area-access-requests.md` §5): answering a request for agent access, and
  // letting agents do it. `access_request.decide` is carried by the roles that assign roles and by the
  // seeded `access-request-approver`, which only an explicit binding gives an agent; no default role gives
  // one. `access_request.allow_agents` is the Owner's alone: it changes who may grant, so it is the switch.
  'access_request.decide',
  'access_request.allow_agents',
  // F-0714 (`concept-machine-relay.md` §4.12): a work area's policy for the machine relays people run
  // (allowed, not allowed, only these, required), and the default of a relay owner's share into it.
  // "Owns or administers" in Jay's words: the Admin's and the Owner's, not a Manager's.
  'relay.manage',
  'policy.manage',
  'part.manage',
  'view.manage',
  'role.read',
  'role.assign',
  'role.manage',
  // F-0800 (ADR-204 §4): changing a person's membership type in an organisation, its end, and removing
  // them from it. The organisation Owner's and Manager's, asked on the organisation (a root).
  'membership.manage',
  'verification.mint',
  'agent_usage.report',
  'agent_usage.configure',
  'decision.answer',
  'installation.manage',
  'billing.manage',
  'user.manage',
] as const;
export type PermissionType = (typeof PERMISSIONS)[number];

/**
 * Shown beside each permission in the admin UI. A permission nobody can describe is one nobody can
 * responsibly grant, so every entry here is required rather than optional.
 */
export const PERMISSION_DESCRIPTIONS: Record<PermissionType, string> = {
  'artifact_share.read':
    'Read an artifact shared with this verified person, within its active grant',
  'artifact_share.contribute': 'Contribute to this verified person’s active artifact grant',
  'file_review.read': 'Read repository file reviews',
  'file_review.contribute': 'Contribute repository file reviews',
  'file_review.manage': 'Manage repository file review decisions',
  'work_item.read': 'See work items and their history',
  'work_item.read_shared':
    'See the work items shared with a guest role one holds here, and what is addressed to them',
  'work_item.create': 'Create work items',
  'work_item.update': 'Change work items, including state and ownership',
  'work_item.delete': 'Delete work items',
  'work_item.comment': 'Comment on work items',
  'work_item.attach': 'Attach files and documents to work items',
  'subagent.comment': 'Let their subagents comment on any work item they may comment on themselves',
  'subagent.update_held':
    'Let their subagents change the work items the subagents hold, and take over their own unheld ones',
  'subagent.close_as_verifier':
    'Let their subagents close a work item as its independent verifier, with a QA verdict',
  'release.read': 'See releases and the roadmap',
  'release.manage': 'Create, update and delete releases',
  'schedule.manage':
    'Create, change and retire the work area’s schedules: the regular work it expects, who answers for it, and when it is reviewed',
  'schedule.run':
    'Ask a machine to run one of the work area’s schedules now, beside its cadence; the machine still decides (§8.5)',
  'schedule.instruct':
    'Make and change the schedules FEATit triggers that send agents an instruction; instructing a role, every lane or another person’s agent also needs work area management',
  'schedule.report':
    'Let a bridge report the work area’s schedules its machine holds, and the runs the machine recorded; it changes none of them',
  'agent.read': 'See the agent roster',
  'agent.create': 'Create agents directly, without an approval request',
  'agent.approve': 'Approve or decline a request to create an agent',
  'agent.input':
    "Type into a watched session's terminal, for a session held by someone else; the session's own owner never needs it",
  'agent.pause':
    "Pause and resume other people's agents in this work area, or the whole work area; one's own agents never need it",
  'work_area.read': 'See a work area and its settings',
  'work_area.manage': 'Change work-area settings and policies',
  'work_area.folder_declare':
    "Add a folder on one's own device to this work area, so one's own agent sessions there belong to it",
  'policy.manage':
    'Change the admission contract — the templates and bindings an item must satisfy',
  'work_area.export': 'Extract this work area in bulk, as CSV or JSON',
  'work_area.publish':
    'Make this work area Public, readable by anyone without an account, and mark comments public',
  'trust_source.allow_agents':
    "Let this work area's agents pause and resume its trust sources, and stop them again",
  'as_if.allow': 'Turn on or off whether this work area may be started as if one held another role',
  'as_if.start':
    'Start this work area as if one held another role or were a named member, with one’s own account, where the setting allows it',
  'access_request.decide':
    'Answer requests for agent access to this work area, within the bounds set for agents',
  'access_request.allow_agents':
    'Let authorized agents answer requests for agent access to this work area, or only people',
  'relay.manage':
    "Set this work area's policy for the machine relays its members run, and have one's own relay shared into it by default",
  'part.manage':
    'Edit the part tree: create, rename, move, split, merge, retire, reopen, delete and relate parts, and attach documents to a part. Every role holds it unless it was removed',
  'view.manage': 'Create and change saved views',
  'role.read': 'See roles, their permissions, and who holds them',
  'role.assign': 'Grant and revoke roles for users and agents',
  'role.manage': 'Create roles and change which permissions they grant',
  'membership.manage':
    "Change who a person is to the organisation (Member, Guest, Public participant), set their membership's end, or remove them from it",
  'verification.mint': 'Mint a read-only verification credential, redeemed once by a one-time link',
  'agent_usage.report': "Let a bridge report the token usage of this person's own machines",
  'agent_usage.configure': 'Change how often bridges report token usage, and how it is binned',
  'decision.answer':
    'Answer a decision or question they are responsible for; only the one responsible answers',
  'installation.manage':
    'Change installation-wide settings: work-item defaults, work-area types, who may sign up or create an organisation, handles, and email delivery',
  'billing.manage':
    'Configure billing: the payment provider keys and mode, the plan catalogue, and its events',
  'user.manage':
    "See every account, deactivate one, and export or erase one on the person's behalf",
};

/** The permission that gates the admin UI itself. Named once so no screen re-decides it. */
export const PERMISSION_ADMIN_UI = 'role.read' satisfies PermissionType;

/**
 * Editing a work area's part tree (F-0320, D-0207): creating, renaming, moving, splitting, merging,
 * retiring, reopening, deleting and relating parts, and attaching documents to a part. Placing an
 * item in a part is not this — that is `work_item.update` on the item — and reading the tree is
 * `work_area.read`.
 *
 * Named once so the server's checks and every screen that offers a tree edit ask the same string.
 */
export const PERMISSION_PART_TREE = 'part.manage' satisfies PermissionType;

/** The plain label a role editor shows for {@link PERMISSION_PART_TREE}. */
export const PERMISSION_PART_TREE_LABEL = 'Edit the part tree';

/**
 * Permissions every role holds unless it was removed from that role (D-0207, Jay 2026-09-29: "we
 * have separate permissions for editing the tree, but everyone gets it by default unless it was
 * removed in the respective role").
 *
 * ⭐ **A default, not an implication.** Each role carries the permission as an ordinary grant — in the
 * seeds below and, for the roles an installation already has, in a migration's rows — so
 * `bindingsGrant` stays the one rule and a role listing still shows everything the role does. What
 * this register adds is that these, and only these, may be switched off and on again per role
 * (`PUT /access-control/roles/:id/permissions`); the rest of a role is still set by migration.
 *
 * ⛔ **Not the guest roles.** A guest reads the items shared with its role "and nothing else"
 * (F-0271); it holds no `work_area.read`, so a guest with a tree edit would be editing a tree it
 * cannot see. {@link defaultPermissionsReach} says which roles the default reaches.
 *
 * ⭐ **`agent.create` joined 2026-10-02 (I-0136, concept §11.4 AC-F, §12.4).** The operation already
 * existed and was gated on ownership alone — creating an agent you own — with no permission asked
 * at all, which is exactly the hazard §6.1/§12.4 describe: a System Admin revoking it changed
 * nothing. Wiring it here, rather than scoping it to one work area, keeps today's behaviour: every
 * role that could create its own agents still can, by default. `AgentsService.registerForUser`
 * asks `AccessControlService.canAnywhere` for it, but only where the answer changes anything
 * (`FEATIT_AGENT_CREATION_MODE=off`/`approval`'s bypass) — `user_agents` carries no work area, so
 * there is no scope to ask the question *in*, and `auto` mode (the default) asks nothing.
 *
 * ⛔ **`agent.merge` is not here (revised 2026-10-03, I-0136 consequence 2, second revision).** It
 * briefly joined alongside `agent.create`, then left the whole vocabulary — see `PERMISSIONS`'s own
 * note on why a declared-but-unenforced permission here is not acceptable either: the wide
 * credential ceilings (`@featit/shared`'s `credentials.ts`) assert nothing in `PERMISSIONS` is
 * unenforced, and a permission that does something only for a merge capability nothing builds yet
 * cannot satisfy that honestly. `AgentsService.mergeForUser` keeps merging two of one's own agents
 * on ownership alone, exactly as before `agent.merge` ever existed as a permission.
 */
export const DEFAULT_ROLE_PERMISSIONS: readonly PermissionType[] = [
  PERMISSION_PART_TREE,
  'agent.create',
  // F-0542 (§23): every working role lets its subagents comment and change what they hold, unless
  // it was switched off for the role. The act's own permission is asked first, so a Viewer holding
  // these still neither comments nor changes anything.
  'subagent.comment',
  'subagent.update_held',
  'subagent.close_as_verifier',
];

/** The permissions a write labelled with a subagent is asked (F-0542, §23). */
export const SUBAGENT_COMMENT_PERMISSION = 'subagent.comment' satisfies PermissionType;
export const SUBAGENT_UPDATE_HELD_PERMISSION = 'subagent.update_held' satisfies PermissionType;
/**
 * A subagent closes an item only as its verifier (D-0044), until Jay answers D-0280 (§23.2). Its own
 * permission, so the answer is a change of the default roles rather than of code.
 */
export const SUBAGENT_CLOSE_AS_VERIFIER_PERMISSION =
  'subagent.close_as_verifier' satisfies PermissionType;

export function isDefaultRolePermission(permission: unknown): permission is PermissionType {
  return (
    typeof permission === 'string' &&
    (DEFAULT_ROLE_PERMISSIONS as readonly string[]).includes(permission)
  );
}

/**
 * The permissions a check actually consults today.
 *
 * The catalogue above is the vocabulary the model can express; this is the subset the product
 * currently *enforces*. It is named because an unenforced permission is worse than a missing one: a
 * System Admin who revokes one and changes nothing has every reason to believe they have restricted
 * somebody. Screens that offer a permission must be able to say which kind it is.
 *
 * The gap used to be the whole work-area surface, which was gated by membership grades instead
 * (ADR-034 §6). ADR-035 converted those call sites, so the work-area permissions are now real.
 * `role.manage` is enforced since D-0207: it switches a {@link DEFAULT_ROLE_PERMISSIONS} entry off or
 * on for one role. Roles are still seeded by migration; nothing creates one at runtime yet.
 * `agent.create` is enforced since I-0136 (concept §11.4 AC-F, §12.4), conditioned on
 * `FEATIT_AGENT_CREATION_MODE`: `AgentsService.registerForUser` asks
 * `AccessControlService.canAnywhere` only where the answer changes anything (bypassing `off` or
 * `approval`'s queue), never for ordinary `auto`-mode self-service.
 *
 * ⛔ **`agent.merge` left the vocabulary entirely (revised 2026-10-03, I-0136 consequence 2,
 * second revision) — it is not merely absent from this list.** It briefly joined `PERMISSIONS`
 * and this list both, asked unconditionally at `AgentsController.merge` — and refused a fresh
 * account merging its own two agents, with no binding anywhere, the exact regression AC-F's own
 * fix had already closed for `agent.create`. The fix removed the door check (ownership, enforced
 * by `AgentsService.mergeForUser`, is the only authority anything asks for today), which left
 * `agent.merge` declared in `PERMISSIONS` but absent here — exactly the hazard this whole register
 * exists to name: a System Admin could toggle it on a role and nothing would change. There is no
 * cross-owner merge capability for it to gate, so it was removed from `PERMISSIONS` too, not kept
 * as a declared-but-silent exception. See `concept-access-control.md` §11.4 AC-G's second revision.
 *
 * A backend spec pins this against the real `assert`/`assertAnywhere` call sites, so a permission
 * that starts or stops being enforced cannot drift away from this list silently.
 */
export const ENFORCED_PERMISSIONS: readonly PermissionType[] = [
  // F-0714: asked by the work area's relay policy write and by a relay share's default
  // (`RelaySharesService`).
  'relay.manage',
  // F-0709: `as_if.allow` is asked by the `startAsIf` policy write (`ProjectsService`), `as_if.start` by the
  // as-if mint (`POST /work-areas/:id/as-if`, slice 1).
  'as_if.allow',
  'as_if.start',
  // F-0713: `decide` is asked of the approver's owner at the door (`AgentApproverDoor`); `allow_agents` by
  // the approvers setting's write (`AccessRequestApproversService`).
  'access_request.decide',
  'access_request.allow_agents',
  'file_review.read',
  'file_review.contribute',
  'file_review.manage',
  // F-0617: asked of the credential's own scope, `effectivePermissions` (a named person holds no role).
  'artifact_share.read',
  'artifact_share.contribute',
  'agent.approve',
  'agent.create',
  'agent.input',
  'agent.pause',
  'agent.read',
  'agent_usage.configure',
  'agent_usage.report',
  'billing.manage',
  'decision.answer',
  'installation.manage',
  'part.manage',
  'policy.manage',
  'release.manage',
  'release.read',
  'schedule.manage',
  'schedule.run',
  'schedule.report',
  'schedule.instruct',
  'role.assign',
  'role.manage',
  'role.read',
  // F-0800: asked by the membership type change, its end and a removal (`MembershipController`).
  'membership.manage',
  'subagent.comment',
  'subagent.update_held',
  'subagent.close_as_verifier',
  'user.manage',
  'verification.mint',
  'view.manage',
  'work_area.export',
  'work_area.folder_declare',
  'work_area.manage',
  'work_area.publish',
  'trust_source.allow_agents',
  'work_area.read',
  'work_item.attach',
  'work_item.comment',
  'work_item.create',
  'work_item.delete',
  'work_item.read',
  'work_item.read_shared',
  'work_item.update',
];

export function isPermissionEnforced(permission: PermissionType): boolean {
  return ENFORCED_PERMISSIONS.includes(permission);
}

/**
 * F-0800 (ADR-204, D-0496 recommendation a, not yet answered): whether a permission is **member-only**.
 * Jay, 2026-11-02: _"roles that have member only permissions should not be assignable to guests and
 * public"_. A role is guest-assignable exactly when none of its permissions is member-only
 * ({@link roleHoldsMemberOnlyPermission}); that is computed from this, never stored as a list of roles.
 *
 * Member-only: managing a work area and every admin or owner right, inviting and assigning roles,
 * settings, billing, the installation, and reads beyond what is shared or joined. Not member-only:
 * reading what is shared or the work area one is in, filing, commenting, and editing or following
 * one's own requests and items, with what a Viewer and a Contributor do there besides. A namespace
 * right is member-only by where it is held: a type's `role_scopes` (Guest and Public participant:
 * work areas only).
 *
 * ⛔ A `Record` over every permission, so a new permission cannot be added without being classed.
 * This is data: D-0496's answer is an edit of these values.
 */
export const MEMBER_ONLY_PERMISSIONS: Readonly<Record<PermissionType, boolean>> = {
  'artifact_share.read': false,
  'artifact_share.contribute': false,
  'file_review.read': false,
  'file_review.contribute': false,
  'file_review.manage': true,
  'work_item.read': false,
  'work_item.read_shared': false,
  'work_item.create': false,
  'work_item.update': false,
  'work_item.delete': false,
  'work_item.comment': false,
  'work_item.attach': false,
  'subagent.comment': false,
  'subagent.update_held': false,
  'subagent.close_as_verifier': false,
  'release.read': false,
  'release.manage': true,
  'schedule.manage': true,
  'schedule.run': true,
  'schedule.instruct': true,
  'schedule.report': false,
  'agent.read': false,
  'agent.create': false,
  'agent.approve': true,
  'agent.input': true,
  'agent.pause': true,
  'work_area.read': false,
  'work_area.manage': true,
  'work_area.folder_declare': false,
  'work_area.export': false,
  'work_area.publish': true,
  'trust_source.allow_agents': true,
  'as_if.allow': true,
  'as_if.start': true,
  'access_request.decide': true,
  'access_request.allow_agents': true,
  'relay.manage': true,
  'policy.manage': true,
  'part.manage': false,
  'view.manage': false,
  'role.read': true,
  'role.assign': true,
  'role.manage': true,
  'membership.manage': true,
  'verification.mint': true,
  'agent_usage.report': false,
  'agent_usage.configure': true,
  'decision.answer': false,
  'installation.manage': true,
  'billing.manage': true,
  'user.manage': true,
};

export function isMemberOnlyPermission(permission: string): boolean {
  // Fail closed: a permission this build does not know is member-only.
  return isPermission(permission) ? MEMBER_ONLY_PERMISSIONS[permission] : true;
}

/** Whether a role carries any member-only permission: then only a type that may hold one may hold it. */
export function roleHoldsMemberOnlyPermission(permissions: readonly string[]): boolean {
  return permissions.some(isMemberOnlyPermission);
}

export interface IRoleSeed {
  key: string;
  name: string;
  description: string;
  scope: PermissionScopeType;
  permissions: readonly PermissionType[];
  /**
   * The subject types this role is written for (I-0136).
   *
   * ⭐ **Declared, because it was previously only in the prose.** `project-agent`'s description says
   * "An agent working in a work area" and nothing could read that — so a role written for agents,
   * seeded on 2026-09-01 and bound to nothing ever since, was indistinguishable from one written for
   * people. A field a test can read is the difference between a claim and a rule.
   *
   * ⛔ Not an authorisation. This says who a role is *for*, so a mismatch can be reported; what a
   * subject may actually do is `bindingsGrant`, and `grant()` still validates the pairing itself.
   */
  subjects: readonly SubjectTypeType[];
}

/**
 * The subject types authority is actually asked about in backend production source.
 *
 * ⛔ **A register, not an aspiration** — the same device as `ENFORCED_PERMISSIONS`, and for the same
 * reason. §2 of `concept-access-control.md` says a subject is a user or an agent with no second
 * mechanism for the second kind. Measured 2026-09-10: **every** authority call site passed
 * `{ type: 'user' }`, every `type: 'agent'` subject in the backend lived inside a `.spec.ts`, and
 * `subject_roles` held zero agent rows (I-0136).
 *
 * ⭐ **`agent` joined as of I-0610 (F-0219 step 0).** §11.6's blocking decision — an agent was named
 * in a request body, with no credential that was one, so resolving authority from the agent alone
 * would have let an owner pick whichever of their own agents held the most — is answered:
 * `auth_sessions.agent_id` names the agent on the credential, and
 * `AccessControlService.requestAccessSubject` resolves the request's subject from it, reaching
 * `narrowToOwner` (`effective(agent) = bindings(agent) ∩ authority(owner)`, ADR-051 §12.1) for the
 * first time in production. §12.1 already stated the rule; this is what gives it a caller.
 *
 * This stays a register rather than a requirement: a scan reads the real call sites and fails if
 * this list and the source disagree in either direction (`subject-reachability.guard.spec.ts`), so a
 * second call site, or a renamed one, has to be reflected here by somebody who means it — the same
 * property that kept `agent`'s absence honest before this widened.
 */
export const ASKED_SUBJECT_TYPES: readonly SubjectTypeType[] = ['user', 'agent'];

export const WORK_ITEM_WORKING_SET = [
  'file_review.read',
  'file_review.contribute',
  'work_item.read',
  // Reading all of it includes what is shared with guests (F-0271); see GUEST_ROLE_SEEDS.
  'work_item.read_shared',
  'work_item.create',
  'work_item.update',
  'work_item.comment',
  'work_item.attach',
  // Jay, 2026-09-17 (decision 38): people and agents who work in an area answer the decisions they are
  // responsible for. Responsibility is what narrows it, not a smaller role.
  'decision.answer',
  'release.read',
  'work_area.read',
  'agent.read',
  // D-0207: every role edits the part tree unless it was removed from the role.
  'part.manage',
  // I-0136: every role creates its own agents unless it was removed from the role.
  'agent.create',
  // F-0542: every role lets its subagents comment and change what they hold unless it was removed.
  'subagent.comment',
  'subagent.update_held',
  'subagent.close_as_verifier',
] as const satisfies readonly PermissionType[];

/**
 * The work-area membership grades, as roles (ADR-035).
 *
 * These sets are not designed, they are **derived**: every one of the 118 places that used to assert
 * a membership grade was read, and the permission it protects put into the set for the grade it
 * demanded. The migration must not change who may do what, so a set that looked tidier than the call
 * sites would be a set that locked somebody out.
 *
 * Three sets, five roles. Only `viewer`, `contributor` and `manager` are ever demanded as a minimum
 * anywhere in the product — nothing asks for `admin` or `owner` — so those two hold the manager set.
 * That is the honest state rather than an invented distinction, and it is now a thing that can be
 * changed by granting rather than by adding a level to a ladder.
 */
export const WORK_AREA_VIEWER_SET = [
  'file_review.read',
  'work_item.read',
  // Reading all of it includes what is shared with guests (F-0271) — and holding it is what lets a
  // Manager grant a guest role, since `assignmentRefusal` refuses a role carrying a permission the
  // grantor does not hold.
  'work_item.read_shared',
  'release.read',
  'work_area.read',
  'agent.read',
  // Reporting one's OWN machine's token usage (F-0188). Every grade holds it, because every person
  // who runs a bridge reports. Reading usage is not a permission of its own: a work area's usage is
  // read with `work_area.read` (Jay, 2026-09-17, concept-agent-token-usage §3.3).
  'agent_usage.report',
  // D-0207 (Jay, 2026-09-29): every role edits the part tree unless it was removed from the role —
  // the viewer included, so every grade above it holds it too. See DEFAULT_ROLE_PERMISSIONS.
  'part.manage',
  // I-0136: every grade creates its own agents unless it was removed — the viewer included, so
  // every grade above it holds it too. See DEFAULT_ROLE_PERMISSIONS.
  'agent.create',
  // F-0542: the viewer included, as for the two above; the act's own permission is asked first.
  'subagent.comment',
  'subagent.update_held',
  'subagent.close_as_verifier',
] as const satisfies readonly PermissionType[];

export const WORK_AREA_CONTRIBUTOR_SET = [
  'file_review.contribute',
  ...WORK_AREA_VIEWER_SET,
  'work_item.create',
  'work_item.update',
  'work_item.comment',
  'work_item.attach',
  // Decision 38: every role that edits items may answer the decisions it is responsible for.
  'decision.answer',
  // F-0694 (SCH-3): whoever checks a run in may have their machine's bridge report the runs its engine
  // recorded. Report only, never `schedule.manage`; the bridge ceiling carries it too.
  'schedule.report',
  // Contributor-gated today: `feedback.delete`, `feedback.deleteImage`, `comments.delete`. Included
  // to preserve behaviour, not because deletion feels like a contributor act.
  'work_item.delete',
  // Two things a contributor may do that are not acts on a work item. They were gated at
  // `contributor` and had no name, so they got one rather than being folded into `work_item.*` —
  // bulk extraction in particular is a capability an installation may well want to withhold on its
  // own terms, and a permission that means two things cannot be withheld on either.
  'work_area.export',
  'view.manage',
  // F-0568 (ADR-168 §8, the captain 2026-10-15): a member who works here adds a folder on their own
  // device. Contributor and up, not the viewer: declaring a folder writes to the work area, and a
  // viewer is read-only here. The viewer sees Add disabled with the reason, never hidden.
  'work_area.folder_declare',
] as const satisfies readonly PermissionType[];

export const WORK_AREA_MANAGER_SET = [
  'file_review.manage',
  ...WORK_AREA_CONTRIBUTOR_SET,
  'release.manage',
  // F-0422: a schedule instructs unattended work on somebody's machine, so it is a manager's act.
  'schedule.manage',
  // §8.5: so is asking a machine to run one now.
  'schedule.run',
  // F-0799 (§13a.7): so is a schedule FEATit triggers that instructs agents.
  'schedule.instruct',
  // F-0654: pausing other people's agents, or every agent of the work area, is a manager's act.
  'agent.pause',
  'work_area.manage',
  // Deliberately here and not in the contributor set. An agent that can rewrite the contract it is
  // measured against is not measured, so filing roles do not hold it (F-0061).
  'policy.manage',
  // D-0055 (b), Jay 2026-09-23: Owners and Managers grant roles in their own namespace or work area,
  // "never above your own role". The bound is not this list — every holder of this set holds the same
  // `role.assign` — it is `assignmentRefusal`, which compares the ladder rank of the role being
  // granted with the grantor's own. `role.manage` (defining roles) stays System Admin's alone.
  'role.assign',
  // F-0713 (ADR-187): whoever assigns roles answers requests for agent access, so D-0055's "holds every
  // permission the role carries" lets exactly these roles give an agent the approver role.
  'access_request.decide',
] as const satisfies readonly PermissionType[];

/**
 * F-0800 (D-0503, Jay: _"a Manager Demo role that has access to all the relevant custom and regular
 * views but not the manage permissions etc. that only members have"_, MTY-24): Manager's set less
 * every member-only permission, so a Guest and a Public participant may hold it. ⛔ Derived, never
 * listed: a permission Manager gains is Manager Demo's too unless it is member-only.
 */
export const WORK_AREA_MANAGER_DEMO_SET: readonly PermissionType[] = WORK_AREA_MANAGER_SET.filter(
  (permission) => !MEMBER_ONLY_PERMISSIONS[permission],
);

/**
 * F-0800 (D-0503, MTY-26): the role shares a holder of each role is matched by, beyond its own. A
 * page shared with Manager is shown to Manager Demo, so a Public participant at Manager Demo sees the
 * custom pages and views a Manager is shown. ⛔ For reading pages only, never a permission: nothing
 * that asks what a role may do reads this.
 */
export const SHARES_MATCHED_BY_ROLE: Readonly<Record<string, readonly string[]>> = {
  'work-area-manager-demo': ['work-area-manager'],
};

/** The role keys a reader's page shares are read for: the roles held, and what each one matches. */
export function shareRoleKeysOf(heldRoleKeys: readonly string[]): string[] {
  return [
    ...new Set(heldRoleKeys.flatMap((key) => [key, ...(SHARES_MATCHED_BY_ROLE[key] ?? [])])),
  ].sort();
}

/**
 * The Admin's set: the manager set, `as_if.start` (F-0709) and `relay.manage` (F-0714), the two things
 * an Admin holds that a Manager does not. Starting a work area as another role is the grade-4 act the
 * concept names; the setting that allows it at all is the Owner's (`as_if.allow`). A work area's relay
 * policy is "own or administer" in Jay's words (`concept-machine-relay.md` §4.12).
 */
export const WORK_AREA_ADMIN_SET = [
  ...WORK_AREA_MANAGER_SET,
  'as_if.start',
  // F-0714: the work area's relay policy, and a relay owner's share into it by default.
  'relay.manage',
] as const satisfies readonly PermissionType[];

/**
 * The Owner's set: the Admin's set and `work_area.publish` (F-0338). Making a work area readable by
 * anyone without an account is irreversible once it has been crawled or copied, so it is not a
 * manager's act by default: only an Owner holds it, and anyone else holds it by an explicit grant
 * (a custom role that names it). The control is disabled with a reason for everyone else, never
 * hidden.
 */
export const WORK_AREA_OWNER_SET = [
  ...WORK_AREA_ADMIN_SET,
  'work_area.publish',
  // T-0604 (D-0125): a Manager pauses and resumes themselves, but handing that to every agent of
  // the work area lifts the agent ceiling for one operation, so it is the Owner's, like Public.
  'trust_source.allow_agents',
  // F-0709 (D-0395): turning on whether the work area may be started as another role changes who can
  // act here under another role's name, so it is the Owner's, as `trust_source.allow_agents` is.
  'as_if.allow',
  // F-0713 (D-0411 a): letting agents answer requests lifts the agent ceiling for one operation, so it is the
  // Owner's, as `trust_source.allow_agents` is. A Manager answers but may not let agents answer.
  'access_request.allow_agents',
] as const satisfies readonly PermissionType[];

/**
 * F-0800 (ADR-204 §4): the permissions only the namespace tier of the ladder adds to the work-area sets.
 * `membership.manage` is asked on the organisation and means nothing on a work area, so the namespace
 * Manager and Owner hold it and their work-area namesakes do not (R-10 otherwise holds: one ladder).
 */
export const NAMESPACE_ONLY_PERMISSIONS = [
  'membership.manage',
] as const satisfies readonly PermissionType[];

/** The role granted to the creator of a new organisation, in the same transaction that creates it. */
export const NAMESPACE_OWNER_ROLE_KEY = 'namespace-owner';

/**
 * F-0412 (§10.13 TO-6): what an outgoing Owner becomes the moment ownership transfers to somebody
 * else, in the same transaction — never left with no role at all, and never left Owner twice over.
 */
export const NAMESPACE_MANAGER_ROLE_KEY = 'namespace-manager';

/**
 * The four roles that stand for a namespace membership grade — the namespace-scope mirror of
 * {@link WORK_AREA_GRADE_ROLE_KEYS}. Named separately for the day a namespace roster writes these the
 * way `project_members` writes the work-area ones; nothing does yet.
 */
export const NAMESPACE_GRADE_ROLE_KEYS: readonly string[] = [
  'namespace-viewer',
  'namespace-contributor',
  'namespace-manager',
  'namespace-owner',
];

/**
 * The five roles that stand in for a membership grade, and nothing else.
 *
 * Membership writes rewrite the holder's binding, and they must rewrite *only* these. A
 * `project-user` binding somebody granted deliberately is not a membership grade and is not this
 * code's to revoke — narrowing the blast radius by key is what keeps "changed a member's grade" from
 * meaning "dropped every role they held here".
 */
export const WORK_AREA_GRADE_ROLE_KEYS: readonly string[] = [
  'work-area-viewer',
  'work-area-contributor',
  'work-area-manager',
  // F-0800 (D-0503, MTY-24): Manager less the member-only permissions.
  'work-area-manager-demo',
  'work-area-admin',
  'work-area-owner',
];

/** The role a `project_members.role` value becomes. `member` is the retired spelling of contributor. */
export const WORK_AREA_ROLE_KEY_BY_GRADE: Readonly<Record<string, string>> = {
  viewer: 'work-area-viewer',
  contributor: 'work-area-contributor',
  member: 'work-area-contributor',
  manager: 'work-area-manager',
  'manager-demo': 'work-area-manager-demo',
  admin: 'work-area-admin',
  owner: 'work-area-owner',
};

/**
 * The role every authenticated subject holds in an **`internal`** work area, without a stored
 * binding (ADR-035).
 *
 * This is the one place a permission arrives without a binding, and it is a stated rule rather than a
 * default: "a work area visible to the whole deployment is readable by the whole deployment".
 * Absence stays denial everywhere else. Resolving it instead of seeding it matters — a stored binding
 * per user per area would be wrong the moment somebody signs up.
 *
 * ⭐ **Renamed from `WORKSPACE_VISIBLE_IMPLICIT_ROLE_KEY`**, because the old name described the level
 * this rule fires for and that level no longer exists. A constant whose name says `workspace` while
 * the value it is compared against says `internal` is the same defect this area already had once —
 * the visibility constant sat under the right name with the wrong contents — and a reader who greps
 * `workspace` to find the implicit-grant rule must not come up empty.
 *
 * ⛔ **It fires for `internal` and for nothing else.** `private` must not reach it, which is the
 * entire go-live fix: it is what stops every authenticated account on the deployment from reading a
 * work area it was never added to.
 */
export const INTERNAL_VISIBLE_IMPLICIT_ROLE_KEY = 'work-area-viewer';

export const GUEST_ROLE_KEYS = [
  'work-area-customer',
  'work-area-tester',
  'work-area-partner',
] as const;
export type GuestRoleKeyType = (typeof GUEST_ROLE_KEYS)[number];

export function isGuestRoleKey(value: unknown): value is GuestRoleKeyType {
  return typeof value === 'string' && (GUEST_ROLE_KEYS as readonly string[]).includes(value);
}

/**
 * Whether {@link DEFAULT_ROLE_PERMISSIONS} reach this role: every role, built-in or defined later, for
 * people or for agents — except a guest role, which reads what is shared with it and nothing else.
 */
export function defaultPermissionsReach(roleKey: string): boolean {
  return !isGuestRoleKey(roleKey) && !NARROW_ROLE_KEYS.includes(roleKey);
}

/**
 * Roles that carry the one permission they are named for and nothing a default would add: the approver
 * role an agent holds beside `project-agent` to answer access requests (F-0713 slice 8, AC-SV15). It holds
 * `access_request.decide` alone, so the defaults never reach it.
 */
const NARROW_ROLE_KEYS: readonly string[] = [ACCESS_APPROVER_ROLE_KEY];

/** `partner` or `work-area-partner` → `work-area-partner`; anything else → `undefined`. */
export function parseGuestRoleKey(value: unknown): GuestRoleKeyType | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim().toLowerCase();
  const key = trimmed.startsWith('work-area-') ? trimmed : `work-area-${trimmed}`;
  return isGuestRoleKey(key) ? key : undefined;
}

/** `work-area-partner` → `partner`, the word the CLI and the web say. */
export function guestRoleWord(key: GuestRoleKeyType): string {
  return key.slice('work-area-'.length);
}

export function isPermission(value: unknown): value is PermissionType {
  return typeof value === 'string' && (PERMISSIONS as readonly string[]).includes(value);
}

/** `undefined` for anything outside the vocabulary — never coerced to a default. */
export function parsePermission(value: unknown): PermissionType | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return isPermission(trimmed) ? trimmed : undefined;
}

export function isSubjectType(value: unknown): value is SubjectTypeType {
  return typeof value === 'string' && (SUBJECT_TYPES as readonly string[]).includes(value);
}

export function isPermissionScope(value: unknown): value is PermissionScopeType {
  return typeof value === 'string' && (PERMISSION_SCOPES as readonly string[]).includes(value);
}

export function permissionDescription(permission: PermissionType): string {
  return PERMISSION_DESCRIPTIONS[permission];
}

/**
 * What each permission lets somebody do, as the end of "Needs permission to …" (F-0324).
 *
 * ⭐ Jay, 2026-09-30: *"read and edit permissions are separate. no edit but read should still keep
 * the elements visible"*, and *"dont clutter the ui with explanations why something is disabled"*.
 * So a control the reader may not use is shown disabled, and says why only in a short tooltip —
 * this sentence, one line and neutral. Every screen and platform takes it from here, so the wording
 * lives in one place per permission.
 *
 * Built in calls marked pure (I-1971, I-2453): a bundle that never reads the table (the web's launch
 * entry) leaves it and the words of `permissions` out.
 */
export const PERMISSION_ACTION_LABELS: Record<PermissionType, string> =
  /* @__PURE__ */ Object.fromEntries(
    /* @__PURE__ */ PERMISSIONS.map((permission) => [
      permission,
      permissionsMessages.action[permissionActionName(permission)],
    ]),
  ) as Record<PermissionType, string>;

type PermissionActionNameType = keyof typeof permissionsMessages.action;

function permissionActionName(permission: PermissionType): PermissionActionNameType {
  return permission.replace(/\./g, '_') as PermissionActionNameType;
}

/** ASF-19: the catalogue key of a permission's readable name, for a sentence in the reader's language. */
export function permissionActionKey(
  permission: PermissionType,
): `permissions.action.${PermissionActionNameType}` {
  return `permissions.action.${permissionActionName(permission)}`;
}

/** The one-line tooltip a control shows when the reader lacks `permission` (F-0324). */
export function permissionNeeded(permission: PermissionType): string {
  return `Needs permission to ${PERMISSION_ACTION_LABELS[permission]}`;
}

/**
 * The permissions that, among the standard roles, only an Owner holds: the Owner set less the
 * Admin's (`work_area.publish`, `trust_source.allow_agents`, `as_if.allow`, `access_request.allow_agents`). A refusal of one of these names an
 * authority rather than a permission, because "needs permission" tells a Manager nothing about whom
 * to ask (I-1462, concept-access-control §19).
 */
export const OWNER_ONLY_PERMISSIONS: readonly PermissionType[] = WORK_AREA_OWNER_SET.filter(
  (permission) => !(WORK_AREA_ADMIN_SET as readonly PermissionType[]).includes(permission),
);

/** The two Owner roles: of the work area, and of a namespace above it. A refusal calls both "the Owner". */
export const OWNER_ROLE_KEYS: readonly string[] = ['work-area-owner', NAMESPACE_OWNER_ROLE_KEY];

/** A role that holds a permission in a work area, as a refusal names it. */
export interface IPermissionHolderRole {
  key: string;
  name: string;
}

function withArticle(roleName: string): string {
  return `${/^[aeiou]/i.test(roleName) ? 'an' : 'a'} ${roleName}`;
}

/**
 * The one-line reason for an Owner-only permission, naming the roles that actually hold it here
 * (I-1462): only the Owner's → *Only the work area's Owner can let agents pause sources*; the Owner's
 * and a custom role's → *Only the Owner or a Release manager can let agents pause sources*. `null`
 * when the holders are not known (an older server) or none is a role of this work area, and the
 * caller falls back to {@link permissionNeeded} rather than guessing who holds it.
 */
export function onlyHoldersCan(
  permission: PermissionType,
  holders: readonly IPermissionHolderRole[] | null | undefined,
): string | null {
  if (!holders?.length) return null;
  const action = PERMISSION_ACTION_LABELS[permission];
  const ownerHolds = holders.some((role) => OWNER_ROLE_KEYS.includes(role.key));
  const others = [
    ...new Set(
      holders.filter((role) => !OWNER_ROLE_KEYS.includes(role.key)).map((role) => role.name),
    ),
  ].sort((left, right) => left.localeCompare(right));
  if (!others.length) return `Only the work area's Owner can ${action}`;
  const names = [...(ownerHolds ? ['the Owner'] : []), ...others.map(withArticle)];
  const list =
    names.length === 1
      ? names[0]
      : `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`;
  return `Only ${list} can ${action}`;
}

export interface IRoleBinding {
  roleKey: string;
  permissions: readonly string[];
  scope: PermissionScopeType;
  /** Null for a system-scoped binding, which covers every work area. */
  scopeId: string | null;
}

/**
 * The thing authority is being asked about: a scope, plus what contains it.
 *
 * ⭐ **`namespaceIds` is why this is a shape and not two arguments.** A namespace-scoped binding
 * covers a work area *beneath* it, and `bindingsGrant` cannot discover that on its own — it is a pure
 * function over bindings and has no way to read a work area's parentage. So the caller, which already
 * loaded the work area to ask the question, supplies the chain.
 *
 * ⛔ **Absent means empty, never "unknown, so allow".** A caller that omits `namespaceIds` gets no
 * namespace grants at all rather than all of them. Every call site that predates the namespace tier
 * passes nothing, and each one must keep resolving exactly as it did — a widening that arrives by
 * default is a widening nobody reviewed.
 */
export interface IPermissionScopeRequest {
  type: PermissionScopeType;
  id?: string | null;
  /**
   * The namespaces that contain this scope, if any — for a work area, the namespace that owns it and
   * each of that namespace's ancestors. Order carries no meaning: resolution is a union, so a match
   * anywhere in the chain is a match.
   */
  namespaceIds?: readonly string[] | null;
  /**
   * The work areas above this one whose grants reach it (D-0054) — the parent, the parent's parent,
   * and so on, **stopping at the first work area that has opted out of inheriting**. Resolved by the
   * caller for the same reason `namespaceIds` is: this function cannot read a work area's parentage.
   *
   * ⛔ Ancestors only, never descendants. A grant on a parent reaches its children; a grant on a
   * child says nothing about its parent (AC-I6). Absent means empty, never "unknown, so allow".
   */
  workAreaIds?: readonly string[] | null;
}

/**
 * The whole authorization rule, in one function so it cannot be reimplemented differently anywhere.
 *
 * Union, never subtraction: a subject holds a permission if ANY binding grants it in a scope that
 * covers the request. There are no deny rules — a deny that overrides a grant makes the answer depend
 * on evaluation order, and every later audit then begins by reconstructing that order.
 *
 * A system-scoped binding answers a work-area-scoped question. Not the reverse: a grant in one work
 * area says nothing about another, and nothing about the installation.
 */
export function bindingsGrant(
  bindings: readonly IRoleBinding[],
  permission: PermissionType,
  scope: IPermissionScopeRequest,
): boolean {
  return bindings.some(
    (binding) => binding.permissions.includes(permission) && bindingCovers(binding, scope),
  );
}

/** What {@link namespaceAncestryIdsFrom} needs of a namespace — nothing but its own parent link. */
export interface INamespaceParentLink {
  id: string;
  parentId: string | null;
}

/** `namespaceAncestryIdsFrom`'s index — build once per batch, not once per namespace asked about. */
export function namespaceParentIndex(
  namespaces: readonly INamespaceParentLink[],
): ReadonlyMap<string, INamespaceParentLink> {
  return new Map(namespaces.map((node) => [node.id, node] as const));
}

/**
 * `namespaceId`, then its parent, then its parent's parent — nearest first, cycle-safe. Mirrors the
 * backend's own `AccessControlService.namespaceAncestryIds` (I-1054, §10.13) exactly, except this
 * walks an already-loaded namespace index in memory instead of a database query per level — correct
 * for `canManyAt`'s "whole tree, zero additional queries" rule precisely because the caller already
 * has the tree. Build `byId` once with {@link namespaceParentIndex} and reuse it across every
 * namespace in a batch, rather than rebuilding it per call. Feeds `IPermissionScopeRequest.
 * namespaceIds` directly: `bindingsGrant` treats a namespace scope's own id and this chain's
 * entries the same way (`bindingCovers`), so this including `namespaceId` itself at the front is
 * deliberate, not an oversight.
 */
export function namespaceAncestryIdsFrom(
  namespaceId: string | null | undefined,
  byId: ReadonlyMap<string, INamespaceParentLink>,
): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();
  let current = namespaceId ?? null;
  while (current && !seen.has(current)) {
    seen.add(current);
    ids.push(current);
    current = byId.get(current)?.parentId ?? null;
  }
  return ids;
}

/**
 * Whether a binding applies to this question at all, whatever it grants.
 *
 * Split out of `bindingsGrant` because the "never above your own role" rule (D-0055) asks a second
 * question of the same coverage — which of a grantor's roles *apply here* — and a coverage rule
 * written twice is one that comes to disagree with itself.
 */
export function bindingCovers(binding: IRoleBinding, scope: IPermissionScopeRequest): boolean {
  if (binding.scope === 'system') return true;
  if (scope.type === 'system') return false;
  if (binding.scope === 'namespace') return namespaceBindingCovers(binding, scope);
  // A work-area binding answers a work-area question and nothing wider. A grant in one work area
  // says nothing about the namespace above it — the direction that matters, because the reverse is
  // the whole point of the tier and this is the half that must NOT hold.
  if (scope.type !== 'work_area') return false;
  if (!binding.scopeId) return false;
  if (scope.id && binding.scopeId === scope.id) return true;
  // D-0054: a grant on an ancestor work area reaches this one, unless inheritance was broken on the
  // way down — which the caller has already applied by stopping the chain there.
  return (scope.workAreaIds ?? []).includes(binding.scopeId);
}

/**
 * Whether a namespace-scoped binding answers this question.
 *
 * Two ways, and no third: the question is that namespace **or one nested beneath it**, or the
 * question is a work area the namespace contains. ⛔ Never "any work area", which is what a namespace
 * binding would mean if it were treated like a system one — and that mistake passes every test that
 * only ever asks about a work area inside the right namespace, which is why the criterion for it asks
 * about one in the wrong namespace.
 *
 * ⭐ For a namespace question, `namespaceIds` carries the namespace's own ancestors (AC-A12): an
 * organisation Owner is an Owner in each of its subgroups, exactly as it is in each of its work areas.
 * Never the reverse — a subgroup's chain does not contain its children, so a subgroup grant says
 * nothing about the organisation above it.
 */
function namespaceBindingCovers(binding: IRoleBinding, scope: IPermissionScopeRequest): boolean {
  if (!binding.scopeId) return false;
  if (scope.type === 'namespace' && binding.scopeId === scope.id) return true;
  return (scope.namespaceIds ?? []).includes(binding.scopeId);
}

/**
 * The one ladder (R-10), as numbers: the same grade has the same rank at every tier, so a namespace
 * Manager and a work-area Manager compare equal, and a namespace Owner outranks a work-area Admin in
 * every work area beneath the namespace.
 *
 * ⚠️ **Only for "never above your own role" (D-0055).** This is not an authorisation rule — what a
 * role may *do* is its permission list and `bindingsGrant`. A rank says only which roles a holder may
 * hand out. `admin` sits between manager and owner because the roster has always ordered it there;
 * nothing else reads the order.
 *
 * Off the ladder (`project-user`, `project-agent`, any role created later): no rank. Such a role is
 * grantable only by somebody who holds every permission it carries — see {@link assignmentRefusal}.
 */
export const ROLE_LADDER_RANK: Readonly<Record<string, number>> = {
  'work-area-viewer': 1,
  'namespace-viewer': 1,
  'work-area-contributor': 2,
  'namespace-contributor': 2,
  // D-0503: no more than a Contributor may do, so no more to grant than a Contributor's rank.
  'work-area-manager-demo': 2,
  'work-area-manager': 3,
  'namespace-manager': 3,
  'work-area-admin': 4,
  'work-area-owner': 5,
  'namespace-owner': 5,
  'system-admin': 6,
};

/** The rank an Owner holds — the line a Manager may not cross when granting (D-0055). */
export const OWNER_ROLE_RANK = 5;

/** A role's place on the ladder, or null when it is not on it. */
export function roleRank(roleKey: string | null | undefined): number | null {
  if (!roleKey) return null;
  return Object.prototype.hasOwnProperty.call(ROLE_LADDER_RANK, roleKey)
    ? ROLE_LADDER_RANK[roleKey]
    : null;
}

/**
 * The work-area Owner role is never granted — at any scope, by anybody (D-0028, answered **c**):
 * a work area's ownership is `projects.owner_id`, one person, and it moves by transfer.
 */
export const NEVER_GRANTED_ROLE_KEYS: readonly string[] = ['work-area-owner'];

/** What is being handed out: a role, by key, with the scope it is bound at and what it grants. */
export interface IAssignableRole {
  key: string;
  scope: PermissionScopeType;
  permissions: readonly string[];
}

/**
 * Why a grant (or a removal) is refused — a closed set, so a screen can say the right sentence and a
 * test can name the rule that fired.
 */
export const ASSIGNMENT_REFUSALS = [
  'scope-mismatch',
  'never-granted',
  'no-role-assign',
  'above-own-role',
  'permissions-not-held',
] as const;
export type AssignmentRefusalType = (typeof ASSIGNMENT_REFUSALS)[number];

/**
 * The highest ladder rank among the bindings that apply to this scope — inherited ones included, so a
 * namespace Owner is an Owner in every work area and subgroup beneath it (AC-A12), and an Owner of
 * one organisation is nobody in another (AC-A4).
 *
 * Counted only over bindings that also hold `role.assign`: a rank that cannot grant anything is not
 * a rank somebody can grant up to.
 */
export function grantorRank(
  bindings: readonly IRoleBinding[],
  scope: IPermissionScopeRequest,
): number {
  let highest = 0;
  for (const binding of bindings) {
    if (!binding.permissions.includes('role.assign')) continue;
    if (!bindingCovers(binding, scope)) continue;
    highest = Math.max(highest, roleRank(binding.roleKey) ?? 0);
  }
  return highest;
}

/**
 * "Never above your own role" (D-0055, answered **b**): may somebody holding these bindings grant —
 * or remove — this role here? `null` means yes; otherwise the rule that refused.
 *
 * In order, and each is a separate test's reason:
 * 1. the role belongs at this scope (a work-area role is bound on a work area, and so on);
 * 2. it is not the work-area Owner, which is transferred, never granted (D-0028);
 * 3. the grantor holds `role.assign` in a scope that covers this one;
 * 4. a ladder role ranks no higher than the grantor's own highest rank here — a Manager grants up to
 *    Manager, an Admin up to Admin, an Owner up to Owner, a System Admin anything;
 * 5. every permission the role carries is one the grantor holds here. For a ladder role at the
 *    grantor's own tier this is already true; it is what bounds a role with no rank
 *    (`project-user` carries `agent.approve`, which no Manager holds), and a role somebody defines
 *    later with a permission its would-be granter has never had.
 *
 * ⛔ Pure, like `bindingsGrant`: the caller resolves the grantor's bindings (credential-narrowed,
 * owner-capped for an agent) and the scope's containers. A rule that loaded rows could not be tested
 * without a database, and this is the rule the whole of self-service access rests on.
 */
export function assignmentRefusal(
  grantorBindings: readonly IRoleBinding[],
  role: IAssignableRole,
  scope: IPermissionScopeRequest,
): AssignmentRefusalType | null {
  if (role.scope !== scope.type) return 'scope-mismatch';
  if (NEVER_GRANTED_ROLE_KEYS.includes(role.key)) return 'never-granted';
  if (!bindingsGrant(grantorBindings, 'role.assign', scope)) return 'no-role-assign';
  const target = roleRank(role.key);
  if (target !== null && target > grantorRank(grantorBindings, scope)) return 'above-own-role';
  for (const permission of role.permissions) {
    if (!isPermission(permission)) return 'permissions-not-held';
    if (!bindingsGrant(grantorBindings, permission, scope)) return 'permissions-not-held';
  }
  return null;
}

/** The sentence for a refusal, naming what would make it work. */
export function assignmentRefusalMessage(
  refusal: AssignmentRefusalType,
  roleName: string,
  verb: 'grant' | 'remove' = 'grant',
): string {
  switch (refusal) {
    case 'scope-mismatch':
      return `${roleName} is not a role for this place.`;
    case 'never-granted':
      return `${roleName} is not granted: a work area has one owner, and ownership moves by transfer.`;
    case 'no-role-assign':
      return `You cannot ${verb} roles here. An Owner or Manager here, or a System Admin, can.`;
    case 'above-own-role':
      return `You cannot ${verb} ${roleName}: it is above your own role here. An Owner can.`;
    case 'permissions-not-held':
      return `You cannot ${verb} ${roleName}: it carries permissions you do not hold here.`;
  }
}

/**
 * How an installation answers a write from a session with no agent behind it yet.
 *
 * Creation is NOT a work-area act — `user_agents` carries no work area, and work-area assignability
 * is a separate policy — so this is an installation-wide setting rather than a per-area one.
 */
export const AGENT_CREATION_MODES = ['auto', 'approval', 'off'] as const;
export type AgentCreationModeType = (typeof AGENT_CREATION_MODES)[number];

export const AGENT_CREATION_MODE_DESCRIPTIONS: Record<AgentCreationModeType, string> = {
  auto: 'Create the agent on first write. Duplicate display names are reported, not refused.',
  approval:
    'Record a request instead. The write is attributed to the user until somebody holding agent.approve confirms it.',
  off: 'Never create. The write is attributed to the user, and the session reference is kept.',
};

export function parseAgentCreationMode(value: unknown): AgentCreationModeType | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim().toLowerCase();
  return (AGENT_CREATION_MODES as readonly string[]).includes(trimmed)
    ? (trimmed as AgentCreationModeType)
    : undefined;
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * An agent key that is an identifier rather than a name.
 *
 * Three of the four `E: codex` rows on stable were keyed `<uuid>` or `agent-<uuid>` (I-0133): an
 * agent id reached the slot where a session NAME was expected, and auto-creation made a new agent
 * out of it every time. No creation mode makes that legitimate, so it is refused outright rather
 * than gated — a policy setting is for decisions, and this is not one.
 */
export function isIdentifierShapedAgentKey(key: string): boolean {
  const trimmed = key.trim().toLowerCase();
  const withoutPrefix = trimmed.startsWith('agent-') ? trimmed.slice('agent-'.length) : trimmed;
  return UUID_PATTERN.test(withoutPrefix);
}
